import React from 'react';
import _ from 'lodash';
import styled from 'styled-components';

const ReCaptchaArea = styled.div`
  margin-top: 10px;
  margin-bottom: 20px;
`;

const ReCaptchaErrorText = styled.div`
  margin-bottom: 20px;
  font-size: 14px;
`;

class RecaptchaWidget extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      recaptchaToken: '',
      recaptchaError: ''
    };

    this.isUnmounted = false;
    this.recaptchaWidgetId = null;

    this.loadRecaptchaScript = this.loadRecaptchaScript.bind(this);
    this.mountRecaptchaWidget = this.mountRecaptchaWidget.bind(this);
    this.tryRenderRecaptchaWidget = this.tryRenderRecaptchaWidget.bind(this);
    this.onRecaptchaVerified = this.onRecaptchaVerified.bind(this);
    this.onRecaptchaExpired = this.onRecaptchaExpired.bind(this);
    this.setRecaptchaContainerRef = this.setRecaptchaContainerRef.bind(this);
  }

  componentDidMount() {
    this.isUnmounted = false;
    this.mountRecaptchaWidget();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.siteKey !== this.props.siteKey) {
      this.mountRecaptchaWidget();
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
  }

  setRecaptchaContainerRef(domNode) {
    this.recaptchaContainer = domNode;
    this.mountRecaptchaWidget();
  }

  loadRecaptchaScript(scriptUrl, scriptId) {
    return new Promise((resolve, reject) => {
      const existingScript = document.getElementById(scriptId);

      if (existingScript) {
        if (window.grecaptcha && window.grecaptcha.render) {
          resolve();
          return;
        }

        existingScript.addEventListener('load', resolve, { once: true });
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.id = scriptId;
      script.src = scriptUrl;
      script.async = true;
      script.defer = true;
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.body.appendChild(script);
    });
  }

  mountRecaptchaWidget() {
    if (
      _.isEmpty(this.props.siteKey) ||
      !this.recaptchaContainer ||
      this.recaptchaWidgetId !== null
    ) {
      return;
    }

    this.setState({
      recaptchaError: ''
    });

    const googleScriptUrl = 'https://www.google.com/recaptcha/api.js?render=explicit';
    const recaptchaNetScriptUrl = 'https://www.recaptcha.net/recaptcha/api.js?render=explicit';

    this.loadRecaptchaScript(googleScriptUrl, 'google-recaptcha-v2-script')
      .catch(() => this.loadRecaptchaScript(recaptchaNetScriptUrl, 'google-recaptcha-v2-script-fallback'))
      .then(() => this.tryRenderRecaptchaWidget(0))
      .catch(() => {
        if (!this.isUnmounted) {
          this.setState({
            recaptchaError: 'Unable to load reCAPTCHA. Please disable ad blockers or try another network.'
          });
        }
      });
  }

  tryRenderRecaptchaWidget(attempt) {
    if (this.isUnmounted || this.recaptchaWidgetId !== null || !this.recaptchaContainer) {
      return;
    }

    if (!window.grecaptcha) {
      if (attempt < 20) {
        window.setTimeout(() => this.tryRenderRecaptchaWidget(attempt + 1), 150);
      }
      return;
    }

    const renderWidget = () => {
      if (this.isUnmounted || this.recaptchaWidgetId !== null || !this.recaptchaContainer) {
        return;
      }

      if (!window.grecaptcha || typeof window.grecaptcha.render !== 'function') {
        if (attempt < 20) {
          window.setTimeout(() => this.tryRenderRecaptchaWidget(attempt + 1), 150);
        }
        return;
      }

      this.recaptchaWidgetId = window.grecaptcha.render(this.recaptchaContainer, {
        sitekey: this.props.siteKey,
        callback: this.onRecaptchaVerified,
        'expired-callback': this.onRecaptchaExpired,
        'error-callback': this.onRecaptchaExpired
      });
    };

    if (typeof window.grecaptcha.ready === 'function') {
      window.grecaptcha.ready(renderWidget);
      return;
    }

    renderWidget();
  }

  onRecaptchaVerified(token) {
    this.setState({
      recaptchaToken: token,
      recaptchaError: ''
    });
    if (this.props.onTokenChange) {
      this.props.onTokenChange(token);
    }
  }

  onRecaptchaExpired() {
    this.setState({
      recaptchaToken: ''
    });
    if (this.props.onTokenChange) {
      this.props.onTokenChange('');
    }
  }

  render() {
    const { siteKey } = this.props;
    const isRecaptchaEnabled = !_.isEmpty(siteKey);

    if (!isRecaptchaEnabled) {
      return null;
    }

    return (
      <>
        <ReCaptchaArea>
          <div ref={this.setRecaptchaContainerRef} />
        </ReCaptchaArea>
        {
          _.isEmpty(this.state.recaptchaError) ? null :
            <ReCaptchaErrorText>
              {this.state.recaptchaError}
            </ReCaptchaErrorText>
        }
      </>
    );
  }
}

export default RecaptchaWidget;
